import { useRef, useState } from 'react'
import s from './RegistrationForm.scss'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { Container, Col, Row, Form, FormGroup, Input, Button } from 'reactstrap'
import Select from 'react-select'
import { Turnstile } from '@marsidev/react-turnstile'
import { APP_CONSTANTS, URLS } from 'utils/constants'
import {
  fromConstantPropertyToSelectValue,
  fromConstantPropertyToSelectOptions
} from 'utils/serializers'
import { Link } from 'react-router-dom'
import { Spinner } from 'components/ideal-core-ui'

const EMAIL_VALIDATION_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i
const TURNSTILE_SITE_KEY =
  typeof window !== 'undefined' && window.Cypress ? '' : process.env.TURNSTILE_SITE_KEY

const RegistrationForm = props => {
  const {
    verified,
    setVerified,
    step,
    setStep,
    formSubmit,
    verifyEmail,
    emailExists,
    setEmailExists,
    percent,
    timezones
  } = props
  const [turnstileToken, setTurnstileToken] = useState('')
  const turnstileRef = useRef(null)
  const turnstileRequired = Boolean(TURNSTILE_SITE_KEY)
  return (
    <Formik
      props={props}
      initialValues={{
        password: '',
        confirmPassword: '',
        companyEmail: '',
        companyName: '',
        companyType: '',
        firstName: '',
        lastName: '',
        companyInterest: '',
        timezone: '',
        phone: ''
      }}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          await formSubmit(
            { ...values, turnstileToken },
            {
              onError: () => {
                setTurnstileToken('')
                turnstileRef.current?.reset()
              }
            }
          )
        } finally {
          setTimeout(() => setSubmitting(false), 2000)
        }
      }}
      validationSchema={Yup.object().shape({
        companyEmail: Yup.string()
          .matches(EMAIL_VALIDATION_REGEX, 'Invalid email address')
          .required('Email is required!'),
        password: Yup.string().required('Password field is required!'),
        confirmPassword: Yup.string()
          .oneOf([Yup.ref('password'), null], 'Passwords must match')
          .required('Confirm Password is required'),
        firstName: Yup.string().required('First Name field is required!'),
        lastName: Yup.string().required('Last Name field is required!'),
        phone: Yup.string().required('Phone field is required!'),
        companyName: Yup.string().required('Company Name field is required!'),
        companyType: Yup.object().shape({
          label: Yup.string().required('Company Type field is required!'),
          value: Yup.string().required('Company Type field is required!')
        }),
        companyInterest: Yup.object().shape({
          label: Yup.string().required('Company Interest field is required!'),
          value: Yup.string().required('Company Interest field is required!')
        }),
        timezone: Yup.object().shape({
          label: Yup.string().required('Timezone field is required!'),
          value: Yup.string().required('Timezone field is required!')
        })
      })}
    >
      {props => {
        const {
          values,
          touched,
          errors,
          isSubmitting,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue
        } = props
        return (
          <>
            <Container className={s.container}>
              <a
                className={s.header}
                href="https://idealcrm.app/"
                target="_blank"
                rel="noreferrer"
              />
              {step === 1 ? (
                <div className={s.firstStep}>
                  <h4 className={s.bold}>Sign Up for Your Account</h4>
                  <h5>Get Started for Free</h5>
                  <p>Immediate access – no credit card required – no strings attached</p>
                  <div className={s.emailWrapper}>
                    <Input
                      type="text"
                      onChange={({ target }) => {
                        setFieldValue('companyEmail', target.value)
                        setEmailExists(false)
                        setVerified(false)
                      }}
                      onBlur={handleBlur}
                      value={values.companyEmail}
                      name="companyEmail"
                      placeholder="Enter Email"
                      data-cy="email_address_input"
                      className={s.companyEmail}
                      autoComplete="randomtext"
                    />
                    <Button
                      data-cy="verify_email_btn"
                      onClick={() => {
                        if (!!errors.companyEmail || !values.companyEmail) return false
                        verifyEmail(values.companyEmail)
                      }}
                      className={s.onBtn}
                      color="secondary"
                    >
                      OK
                    </Button>
                  </div>
                  {errors.companyEmail && touched.companyEmail && (
                    <div className={s.inputFeedback}>{errors.companyEmail}</div>
                  )}
                  {emailExists && (
                    <div data-cy="email_already_used" className={s.inputFeedback}>
                      This email is already in use. <br />
                      Do you want to <Link to={URLS.LOGIN}>login</Link> instead?
                    </div>
                  )}
                  <Button
                    disabled={!verified}
                    onClick={() => setStep(2)}
                    className={s.loginBtn}
                    type="submit"
                    data-cy="continue_btn"
                  >
                    Continue
                  </Button>
                </div>
              ) : step === 2 ? (
                <Form className={s.form} onSubmit={handleSubmit}>
                  <h4 className={s.title}>We just need a few more details</h4>
                  <Row>
                    <Col>
                      <FormGroup className={s.group}>
                        <Input
                          type="text"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.firstName}
                          name="firstName"
                          placeholder="First Name"
                          disabled={isSubmitting}
                          data-cy="first_name_input"
                          autoComplete="randomtext"
                        />
                        {errors.firstName && touched.firstName && (
                          <div className={s.inputFeedback}>{errors.firstName}</div>
                        )}
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup className={s.group}>
                        <Input
                          type="text"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.lastName}
                          name="lastName"
                          placeholder="Last Name"
                          disabled={isSubmitting}
                          data-cy="last_name_input"
                          autoComplete="randomtext"
                        />
                        {errors.lastName && touched.lastName && (
                          <div className={s.inputFeedback}>{errors.lastName}</div>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <FormGroup className={s.group}>
                        <Input
                          type="text"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.phone}
                          name="phone"
                          placeholder="Phone Number"
                          disabled={isSubmitting}
                          data-cy="company_phone_input"
                          autoComplete="randomtext"
                        />
                        {errors.phone && touched.phone && (
                          <div className={s.inputFeedback}>{errors.phone}</div>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <FormGroup className={s.group}>
                        <Input
                          type="text"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.companyName}
                          name="companyName"
                          placeholder="Company Name"
                          disabled={isSubmitting}
                          data-cy="company_name_input"
                          autoComplete="randomtext"
                        />
                        <span className={s.hint}>
                          This is what your company will be called in iDeal CRM & can’t be changed.
                        </span>
                        {errors.companyName && touched.companyName && (
                          <div className={s.inputFeedback}>{errors.companyName}</div>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <FormGroup className={s.group}>
                        <Select
                          value={
                            values.companyType
                              ? fromConstantPropertyToSelectValue(
                                  values.companyType,
                                  'OrganizationType'
                                )
                              : ''
                          }
                          placeholder="Company Type"
                          isDisabled={isSubmitting}
                          onChange={value => setFieldValue('companyType', value)}
                          options={fromConstantPropertyToSelectOptions(
                            Object.keys(APP_CONSTANTS.OrganizationType),
                            'OrganizationType'
                          )}
                          id="company_type_select_cy"
                        />
                        {errors.companyType && touched.companyType && (
                          <div className={s.inputFeedback}>{errors.companyType.label}</div>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <FormGroup className={s.group}>
                        <Select
                          value={
                            values.companyInterest
                              ? fromConstantPropertyToSelectValue(
                                  values.companyInterest,
                                  'OrganizationInterest'
                                )
                              : ''
                          }
                          placeholder="Select Company Interest"
                          isDisabled={isSubmitting}
                          onChange={value => setFieldValue('companyInterest', value)}
                          options={fromConstantPropertyToSelectOptions(
                            Object.keys(APP_CONSTANTS.OrganizationInterest),
                            'OrganizationInterest'
                          )}
                          id="company_interest_select_cy"
                        />
                        {errors.companyInterest && touched.companyInterest && (
                          <div className={s.inputFeedback}>{errors.companyInterest.label}</div>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <FormGroup className={s.group}>
                        <Select
                          value={values.timezone}
                          placeholder="Select Timezone"
                          isDisabled={isSubmitting}
                          onChange={value => setFieldValue('timezone', value)}
                          options={timezones}
                          id="company_timezone_cy"
                        />
                        {errors.timezone && touched.timezone && (
                          <div className={s.inputFeedback}>{errors.timezone.label}</div>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <FormGroup>
                        <Input
                          type="text"
                          value={values.companyEmail}
                          name="id"
                          disabled
                          autoComplete="username"
                        />
                      </FormGroup>
                      <FormGroup>
                        <Input
                          type="password"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.password}
                          name="password"
                          placeholder="Set your Password"
                          disabled={isSubmitting}
                          data-cy="password_input"
                          autocomplete="current-password"
                        />
                        {errors.password && touched.password && (
                          <div className={s.inputFeedback}>{errors.password}</div>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <FormGroup>
                        <Input
                          type="password"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.confirmPassword}
                          name="confirmPassword"
                          placeholder="Confirm Password"
                          disabled={isSubmitting}
                          data-cy="confirm_password_input"
                          autoComplete="randomtext"
                        />
                        {errors.confirmPassword && touched.confirmPassword && (
                          <div className={s.inputFeedback}>{errors.confirmPassword}</div>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>
                  {turnstileRequired && (
                    <Row>
                      <Col>
                        <FormGroup className={s.group} data-cy="turnstile_widget">
                          <Turnstile
                            ref={turnstileRef}
                            siteKey={TURNSTILE_SITE_KEY}
                            onSuccess={setTurnstileToken}
                            onExpire={() => setTurnstileToken('')}
                            onError={() => setTurnstileToken('')}
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  )}
                  <Button
                    data-cy="submit_btn"
                    className={s.loginBtn}
                    type="submit"
                    disabled={isSubmitting || (turnstileRequired && !turnstileToken)}
                  >
                    {isSubmitting ? <Spinner /> : 'Start Using iDeal CRM'}
                  </Button>
                  <div className={s.clearfix} />
                </Form>
              ) : (
                <div className={s.stepThree}>
                  <h4 className={s.bold}>CREATING YOUR ACCOUNT</h4>
                  <p>
                    Please wait while we are setting things up. <br /> It only takes about 1
                    minute.{' '}
                  </p>
                  <div className={s.chart}>
                    <div className={s.segment} style={{ width: `${percent}%` }} />
                  </div>
                </div>
              )}
              {step !== 3 && (
                <section className={s.bottomInfoConatiner}>
                  <div className={s.termsContainer}>
                    By signing up you agree to our{' '}
                    <a href="https://idealcrm.app/" target="_blank" rel="noreferrer">
                      Terms and Conditions
                    </a>{' '}
                    and{' '}
                    <a href="https://idealcrm.app/" target="_blank" rel="noreferrer">
                      Privacy Policy
                    </a>
                    .
                  </div>
                  <div className={s.links}>
                    <Link to={URLS.LOGIN}>Already have an account? Click here to login</Link>
                    <a
                      style={{ float: 'right' }}
                      href="https://idealcrm.app/"
                      target="_blank"
                      rel="noreferrer"
                    >
                      iDeal CRM website
                    </a>
                  </div>
                  <h4>
                    iDeal CRM makes it easier for construction sales teams to manage bids, jobs,
                    projects, and tasks.
                  </h4>
                </section>
              )}
            </Container>
          </>
        )
      }}
    </Formik>
  )
}

export default RegistrationForm
